import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FollowUpLetterFile } from "../../types/Rows";
import { deleteFulFile, downloadFulFile, toggleFulFileInclude, updateFulFileLabel, uploadFulFile } from "../../api/reportsApi";
import { Button } from "../../components/ui/button";
import { field } from "../../components/ui/styles";
import { failurToast, successToast } from "../../helpers/successToast";

const QUERY_KEY = (reportId: string) => [reportId, "FulFiles"];

function FulFileLabelInput({
  fileId,
  reportId,
  initialLabel,
}: {
  fileId: string;
  reportId: string;
  initialLabel: string;
}) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(initialLabel);
  const [editing, setEditing] = useState(false);

  const { mutate: saveLabel } = useMutation(
    (label: string) => updateFulFileLabel(reportId, fileId, label),
    { onSuccess: () => queryClient.invalidateQueries(QUERY_KEY(reportId)) }
  );

  const commit = () => {
    setEditing(false);
    if (value !== initialLabel) saveLabel(value);
  };

  if (editing) {
    return (
      <input
        autoFocus
        className="w-full text-xs border border-[var(--sc-border)] rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-[var(--sc-amber)]"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") { setValue(initialLabel); setEditing(false); }
        }}
        placeholder="Dodaj opis..."
        maxLength={256}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="w-full text-left text-xs text-[var(--sc-muted)] hover:text-[var(--sc-text)] truncate pl-6"
      title={value || "Kliknij, aby dodać opis"}
    >
      {value || <span className="italic">Dodaj opis…</span>}
    </button>
  );
}

export function FollowUpLetterFiles({
  reportId,
  files,
}: {
  reportId: string;
  files: FollowUpLetterFile[];
}) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: upload } = useMutation(
    async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return uploadFulFile(reportId, formData, file.name);
    },
    {
      onSuccess: () => {
        successToast();
        queryClient.invalidateQueries(QUERY_KEY(reportId));
      },
      onError: () => failurToast("Nie udało się załączyć pliku"),
    }
  );

  const { mutate: toggle } = useMutation(
    async ({ fileId, value }: { fileId: string; value: boolean }) =>
      toggleFulFileInclude(reportId, fileId, value),
    { onSuccess: () => queryClient.invalidateQueries(QUERY_KEY(reportId)) }
  );

  const { mutate: remove } = useMutation(
    async (fileId: string) => deleteFulFile(reportId, fileId),
    {
      onSuccess: () => {
        successToast();
        queryClient.invalidateQueries(QUERY_KEY(reportId));
      },
      onError: () => failurToast("Nie udało się usunąć pliku"),
    }
  );

  const inputCls = "block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em]";

  return (
    <div className="space-y-2">
      <p className={inputCls}>Załączniki:</p>
      {files.length === 0 && (
        <p className="text-sm text-[var(--sc-muted)]">Brak załączników</p>
      )}
      {files.map((f) => (
        <div
          key={f.id}
          className="flex flex-col gap-1 px-3 py-2 rounded-lg border border-[#EBEBEA] bg-white"
        >
          <div className="flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-sm text-[var(--sc-text)] cursor-pointer flex-1 min-w-0">
              <input
                type="checkbox"
                className={field.checkbox}
                checked={f.includeInPdf}
                onChange={(e) => toggle({ fileId: f.id, value: e.target.checked })}
              />
              <span className="truncate">{f.name}</span>
            </label>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => downloadFulFile(reportId, f.id, f.name)}
            >
              Pobierz
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => remove(f.id)}
            >
              Usuń
            </Button>
          </div>
          <FulFileLabelInput
            fileId={f.id}
            reportId={reportId}
            initialLabel={f.label ?? ""}
          />
        </div>
      ))}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload(file);
          e.target.value = "";
        }}
      />
      <Button
        variant="secondary"
        size="sm"
        type="button"
        onClick={() => fileInputRef.current?.click()}
      >
        Dodaj załącznik
      </Button>
    </div>
  );
}

export { QUERY_KEY as FUL_FILES_QUERY_KEY };
